import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import Header from './Header';
import { getUserInfo } from './apiUtils.js';
import DependencyDialog from './DependencyDialog';
import { Plus, Search, AlertTriangle } from 'lucide-react';


// Import the JSON-based configuration
import appConfig, { ENDPOINT_KEYS } from './config/appConfig';


const DialogStyles = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1000
  },
  dialog: {
    backgroundColor: 'white',
    padding: '20px',
    borderRadius: '8px',
    width: '400px',
    boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)',
    display: 'flex',
    flexDirection: 'column',
    gap: '20px'
  },
  title: {
    fontSize: '18px',
    textAlign: 'center',
    margin: 0,
    color: '#000',
    backgroundColor: 'white',
  },
  button: {
    backgroundColor: '#FFFDD0',
    border: 'none',
    borderRadius: '4px',
    padding: '10px',
    width: '100%',
    cursor: 'pointer',
    fontSize: '16px',
    fontWeight: 500,
    color: 'black'
  }
};

const Dialog = ({ message, onClose, onCancel, showOverrideOption = false, onOverrideChange = () => {}, overrideValue = false }) => {
  const isConfirmation = message.startsWith('Are you sure');
  const isEmptyLevels = message.startsWith('No Functional Levels found');

  const formattedMessage = message.split('\n').map((line, i) => (
    <React.Fragment key={i}>
      {line}
      {i < message.split('\n').length - 1 && <br />}
    </React.Fragment>
  ));

  return (
    <div style={DialogStyles.overlay}>
      <div style={{
        ...DialogStyles.dialog,
        borderRadius: isEmptyLevels ? '16px' : '8px',
        border: isEmptyLevels ? '1px solid #ccc' : 'none',
      }}>
        <h2 style={{
          ...DialogStyles.title,
          fontSize: isEmptyLevels ? '16.8px' : '18px',
          lineHeight: isEmptyLevels ? '1.4' : '1.2',
        }}>
          {formattedMessage}
        </h2>

        {isConfirmation && showOverrideOption && (
          <div style={{ marginTop: '10px', marginBottom: '10px' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px' }}>
              <input
                type="checkbox"
                checked={overrideValue}
                onChange={(e) => onOverrideChange(e.target.checked)}
                style={{ cursor: 'pointer' }}
              />
              Override Dependencies
            </label>
          </div>
        )}

        {isConfirmation ? (
          <div style={{ display: 'flex', gap: '10px' }}>
            <button
              style={{
                ...DialogStyles.button,
                backgroundColor: '#FF8F89',
                color: 'white',
                width: '50%'
              }}
              onClick={onClose}
            >
              Yes
            </button>
            <button
              style={{
                ...DialogStyles.button,
                width: '50%'
              }}
              onClick={onCancel}
            >
              No
            </button>
          </div>
        ) : (
          <button
            style={{
              ...DialogStyles.button,
              backgroundColor: isEmptyLevels ? '#FFFDD0' : '#FFFDD0',
              width: isEmptyLevels ? '120px' : '100%',
              margin: isEmptyLevels ? '0 auto' : '0',
              display: 'block'
            }}
            onClick={onClose}
          >
            OK
          </button>
        )}
      </div>
    </div>
  );
};

export { Dialog, DialogStyles };

const CreateLevelDialog = ({ isOpen, onClose, onSubmit, level, isEdit, existingLevels, parentLevelDescription }) => {
  const [description, setDescription] = useState('');
  const [error, setError] = useState('');
  const descriptionInputRef = useRef(null);

  // Set initial description when level changes
  useEffect(() => {
    if (level) {
      setDescription(level.level_description || '');
    } else {
      setDescription('');
    }
  }, [level]);

  // Clear form when opening for create mode
  useEffect(() => {
    if (isOpen && !isEdit) {
      setDescription('');
      setError('');
    } else if (isEdit) {
      setError('');
    }
  }, [isOpen, isEdit]);

  // Focus input when dialog opens
  useEffect(() => {
    if (isOpen && descriptionInputRef.current) {
      descriptionInputRef.current.focus();
    }
  }, [isOpen]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    try {
      console.log('Submitting description:', description);
      await onSubmit({
        level_description: description
      });

      // For create mode, clear form and keep dialog open
      if (!isEdit) {
        setDescription('');
        descriptionInputRef.current?.focus();
      } else {
        // For edit mode, close dialog after successful update
        setTimeout(() => onClose(), 1500);
      }

    } catch (err) {
      console.error('Submit error:', err);
      setError(err.message || `Failed to ${isEdit ? 'update' : 'create'} level`);
    }
  };

  const handleClose = () => {
    setDescription('');
    setError('');
    onClose();
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  if (!isOpen) return null;

  return (
    <div style={DialogStyles.overlay}>
      <div style={{...DialogStyles.dialog, width: '500px'}}>
        <h2 style={{ ...DialogStyles.title, backgroundColor: '#CCCCFF' }}>
          {isEdit ? 'Edit Functional Level' : 'Create New Functional Level'}
        </h2>
        <form onSubmit={handleSubmit}>
          <div style={{marginBottom: '15px'}}>
            <label style={{display: 'block', marginBottom: '5px'}}>Parent Level:</label>
            <input
              type="text"
              value={parentLevelDescription || 'Root Level'}
              readOnly
              style={{
                width: '100%',
                padding: '8px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                backgroundColor: '#f5f5f5',
                color: '#666'
              }}
            />
          </div>
          <div style={{marginBottom: '15px'}}>
            <label style={{display: 'block', marginBottom: '5px'}}>Description*:</label>
            <input
              type="text"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Enter level description"
              style={{
                width: '100%',
                padding: '8px',
                border: '1px solid #ddd',
                borderRadius: '4px'
              }}
              maxLength={300}
              ref={descriptionInputRef}
            />
          </div>

          {/* Error Messages - Only show errors in dialog */}
          {error && (
            <div className="mb-4 p-3 rounded-md bg-red-50 border border-red-200 text-red-800">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm font-medium">
                    {error}
                  </p>
                </div>
              </div>
            </div>
          )}

          <div className="flex justify-center gap-4">
            <button
              type="button"
              onClick={handleClose}
              style={{
                backgroundColor: '#FFFDD0',
                padding: '8px 16px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              Close
            </button>
            <button
              type="submit"
              style={{
                padding: '8px 16px',
                backgroundColor: '#C8F4DE',
                color: 'black',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              {isEdit ? 'Update' : 'Add New Level'}
            </button>
          </div>

          {!isEdit && (
            <p className="text-gray-600 text-sm mt-4 text-center">
              You can continue adding more levels. Click 'Close' when finished.
            </p>
          )}
        </form>
      </div>
    </div>
  );
};

export { CreateLevelDialog };

const FunctionalLevels = () => {
  // State declarations
  const [showDialog, setShowDialog] = useState(false);
  const [dialogMessage, setDialogMessage] = useState('');
  const [redirectPath, setRedirectPath] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState(null);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedLevelForEdit, setSelectedLevelForEdit] = useState(null);
  const [pendingDeleteAction, setPendingDeleteAction] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredLevels, setFilteredLevels] = useState([]);
  const [permissions, setPermissions] = useState({
    canDelete: false,
    canEdit: false,
    canCreate: false,
    canView: true
  });
  const [levels, setLevels] = useState([]);
  const [selectedRows, setSelectedRows] = useState(new Set());
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [corporateAccount, setCorporateAccount] = useState('');
  const [currentProject, setCurrentProject] = useState('');
  const [userId, setUserId] = useState('');
  const [parentOfParent, setParentOfParent] = useState(null);
  const [currentParent, setCurrentParent] = useState(null);
  const [currentParentDescription, setCurrentParentDescription] = useState('');
  const [success, setSuccess] = useState('');
  const [overrideDependencies, setOverrideDependencies] = useState(false);
  const [showDependencies, setShowDependencies] = useState(false);
  const [dependencies, setDependencies] = useState(null);
  const [itemsPerPage, setItemsPerPage] = useState(10);


const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
const [levelToDelete, setLevelToDelete] = useState(null);

// New state for sorting
const [sortField, setSortField] = useState(null);
const [sortDirection, setSortDirection] = useState('asc');

  const login_token = localStorage.getItem('authToken');

  const styles = {
    deleteButton: {
      backgroundColor: '#dc3545',
      color: 'white',
      border: 'none',
      borderRadius: '4px',
      cursor: 'pointer',
      padding: '6px 12px',
      height: '28px',
      fontSize: '0.8rem',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    },
    editButton: {
      backgroundColor: '#4CAF50',
      color: 'white',
      border: 'none',
      borderRadius: '4px',
      cursor: 'pointer',
      padding: '6px 12px',
      height: '28px',
      fontSize: '0.8rem',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    },
    disabled: {
      opacity: 0.6
    },
    container: {
      padding: '20px',
      maxWidth: '1200px',
      margin: '0 auto',
    },
    table: {
      width: '100%',
      borderCollapse: 'collapse',
      marginTop: '0px',
      backgroundColor: '#fff',
      boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
      fontSize: '0.875rem',
    },
    thNonSortable: {
      backgroundColor: '#E5E4E2',
      color: '#0064e5',
      padding: '6px 12px',
      textAlign: 'left',
      borderBottom: '2px solid #ddd',
      fontSize: '0.875rem',
      lineHeight: '1',
      height: '32px'
    },
    th: {
      backgroundColor: '#E5E4E2',
      color: '#0064e5',
      padding: '6px 12px',
      textAlign: 'left',
      borderBottom: '2px solid #ddd',
      fontSize: '0.875rem',
      lineHeight: '1',
      height: '32px'
    },
    td: {
      padding: '12px',
      borderBottom: '1px solid #ddd',
    },



thSortable: {
  backgroundColor: '#E5E4E2',
  color: '#0064e5',
  padding: '6px 12px',
  textAlign: 'left',
  borderBottom: '2px solid #ddd',
  fontSize: '0.875rem',
  lineHeight: '1',
  height: '32px',
  cursor: 'pointer',
  userSelect: 'none',
  position: 'relative'
},


    checkbox: {
      marginRight: '10px',
    },
    button: {
      padding: '6px 12px',
      margin: '0 5px',
      backgroundColor: '#007bff',
      color: 'white',
      border: 'none',
      borderRadius: '4px',
      cursor: 'pointer',
      fontSize: '0.8rem',
      height: '28px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    }
  };

  // Helper functions
  const showErrorDialog = (message, path) => {
    setDialogMessage(message);
    setRedirectPath(path);
    setShowDialog(true);
  };



const renderDeleteConfirmDialog = () => {
  if (!isDeleteConfirmOpen || !levelToDelete) return null;

  return (
    <div style={{...DialogStyles.overlay, zIndex: 1100}}>
      <div style={{...DialogStyles.dialog, width: '600px'}}>
        <div style={{
          backgroundColor: '#FFE6E6',
          color: '#CC0000',
          padding: '15px 0',
          textAlign: 'center',
          fontSize: '18px',
          fontWeight: 'bold',
          borderRadius: '8px 8px 0 0',
          margin: '-20px -20px 20px -20px'
        }}>
          <div className="flex items-center justify-center gap-2">
            <AlertTriangle size={20} />
            <span>Confirm Deletion</span>
          </div>
        </div>

        <div className="px-6 py-4">
          <p className="text-gray-700 mb-4">
            {levelToDelete.level_id === 'multiple'
              ? 'Are you sure you want to delete these functional levels?'
              : 'Are you sure you want to delete this functional level?'}
          </p>
          <p className="text-sm text-red-600 mb-4">
            This action cannot be undone.
          </p>

          {/* Show level details */}
          <div className="bg-gray-50 rounded p-3 mb-4">
            <p className="text-xs text-gray-600 mb-2">Level details:</p>
            <div className="text-sm text-gray-700">
              {levelToDelete.level_id === 'multiple' ? (
                <div><strong>Selected Levels:</strong> {levelToDelete.level_description}</div>
              ) : (
                <>
                  <div><strong>Level Name:</strong> {levelToDelete.level_description}</div>
                  <div><strong>Parent Level:</strong> {levelToDelete.parent_level_description || 'Root Level'}</div>
                  <div><strong>Level ID:</strong> {levelToDelete.level_id}</div>
                </>
              )}
            </div>
          </div>

          {/* Override Dependencies checkbox */}
          <div style={{ marginTop: '10px', marginBottom: '10px' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px' }}>
              <input
                type="checkbox"
                checked={overrideDependencies}
                onChange={(e) => setOverrideDependencies(e.target.checked)}
                style={{ cursor: 'pointer' }}
              />
              Override Dependencies
            </label>
          </div>
        </div>

        <div className="flex justify-center gap-4 p-6 pt-0">
          <button
            onClick={() => {
              setIsDeleteConfirmOpen(false);
              setLevelToDelete(null);
              setOverrideDependencies(false);
            }}
            style={{
              backgroundColor: '#f8f9fa',
              padding: '8px 16px',
              border: '1px solid #ddd',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleDeleteConfirmed}
            style={{
              backgroundColor: '#dc3545',
              color: 'white',
              padding: '8px 16px',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            {levelToDelete.level_id === 'multiple' ? 'Delete Levels' : 'Delete Level'}
          </button>
        </div>
      </div>
    </div>
  );
};

  // Updated permission check function using new API
  const checkPermission = async (categoryHeader) => {
    try {
      const response = await fetch(appConfig.getUrl('/get_user_access_level_new'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': `Bearer ${login_token}`
        },
        body: JSON.stringify({
          corporate_account: corporateAccount,
          user_id: userId,
          project_id: currentProject,
          category_header: categoryHeader,
          category_sub_header: []
        }),
      });
      const data = await response.json();

      // Create new permissions object
      const newPermissions = {
        canCreate: false,
        canEdit: false,
        canDelete: false,
        canView: true
      };

      // Map API response to permission fields
      data.user_actions_list.forEach(item => {
        switch(item.user_action) {
          case 'Create or Edit Functional Heirarchy':
            newPermissions.canCreate = item.has_access;
            newPermissions.canEdit = item.has_access;
            break;
          case 'Delete Functional Heirarchy':
            newPermissions.canDelete = item.has_access;
            break;
        }
      });

      setPermissions(newPermissions);
      console.log('Permissions updated:', newPermissions);

      return data.access_status && data.status === 'Success';
    } catch (error) {
      console.error(`Error checking permission for ${categoryHeader}:`, error);
      return false;
    }
  };

  useEffect(() => {
    console.log(`use Effect 3`);
    console.log(`use Effect 3  : ${currentParentDescription}`);
    console.log(`use Effect 3  : ${currentParent}`);
    console.log(`use Effect 3 : ${parentOfParent}`);

    const checkAllPermissions = async () => {
      if (corporateAccount && userId && currentProject) {
        try {
          await checkPermission('Functional Heirarchy Management');
        } catch (error) {
          console.error('Permission check error:', error);
        }
      }
    };

    checkAllPermissions();
  }, [corporateAccount, userId, currentProject]);

  useEffect(() => {
    console.log(`use Effect 4a`);
    console.log(`use Effect 4a  : ${currentParentDescription}`);
    console.log(`use Effect 4a  : ${currentParent}`);
    console.log(`use Effect 4a : ${parentOfParent}`);

    if (searchQuery === '' && corporateAccount && currentProject) {
      console.log(`Inside use Effect 4a after searchQuery success validation : ${currentParent}`);
      fetchLevels(currentParent);
    }
  }, [searchQuery]);

  useEffect(() => {
    console.log(`use Effect 4b`);
    console.log(`use Effect 4b  : ${currentParentDescription}`);
    console.log(`use Effect 4b  : ${currentParent}`);
    console.log(`use Effect 4b : ${parentOfParent}`);
  }, [currentParent, parentOfParent, currentParentDescription]);

  const handleSearch = () => {
    // Call fetchLevels with the current parent level ID to include search query
    setCurrentParentDescription('');
    console.log(`handleSearch currentParentDescription  : ${currentParentDescription}`);
    console.log(`handleSearch currentParent  : ${currentParent}`);
    console.log(`handleSearch parentOfParent  : ${parentOfParent}`);
    fetchLevels();
  };




// Sorting function
const handleSort = (field) => {
  let direction = 'asc';
  if (sortField === field && sortDirection === 'asc') {
    direction = 'desc';
  }
  setSortField(field);
  setSortDirection(direction);
};

// Sort the levels based on current sort settings
const getSortedLevels = (levelsToSort) => {
  if (!sortField) return levelsToSort;

  return [...levelsToSort].sort((a, b) => {
    let aValue = '';
    let bValue = '';

    switch (sortField) {
      case 'description':
        aValue = a.level_description || '';
        bValue = b.level_description || '';
        break;
      case 'parent':
        aValue = a.parent_level_description || '';
        bValue = b.parent_level_description || '';
        break;
      default:
        return 0;
    }

    // Convert to lowercase for case-insensitive sorting
    aValue = aValue.toLowerCase();
    bValue = bValue.toLowerCase();

    if (sortDirection === 'asc') {
      return aValue.localeCompare(bValue);
    } else {
      return bValue.localeCompare(aValue);
    }
  });
};

// Render sort icon
const renderSortIcon = (field) => {
  if (sortField !== field) {
    return <span style={{ opacity: 0.3, marginLeft: '4px' }}>⇅</span>;
  }
  return (
    <span style={{ opacity: 1, marginLeft: '4px' }}>
      {sortDirection === 'asc' ? '⇈' : '⇊'}
    </span>
  );
};



  const handleCreateLevel = async (levelData) => {
    if (!permissions.canCreate) {
      setDialogMessage('Insufficient permissions to create level');
      setShowDialog(true);
      return Promise.reject(new Error('Insufficient permissions'));
    }

    try {
      const response = await fetch(appConfig.getUrl('/create_functional_level'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': `Bearer ${login_token}`
        },
        body: JSON.stringify({
          corporate_account: corporateAccount,
          project_id: currentProject,
          parent_level_id: currentParent,
          level_description: levelData.level_description
        }),
      });

      const data = await response.json();

      if (data.status === 'Success') {
        // Set success message in main component
        setSuccess('Level created successfully!');
        setError(''); // Clear any existing errors

        // Clear success message after 3 seconds
        setTimeout(() => setSuccess(''), 3000);

        // Refresh the levels list
        setSelectedRows(new Set());
        fetchLevels(currentParent);

        return Promise.resolve();

      } else {
        return Promise.reject(new Error(data.status_description || 'Failed to create level'));
      }
    } catch (err) {
      setError(err.message || 'Failed to create level');
      setSuccess('');
      return Promise.reject(new Error(err.message || 'Failed to create level'));
    }
  };

  const handleEditLevel = async (levelData) => {
    if (!permissions.canEdit) {
      return Promise.reject(new Error('Insufficient permissions to edit level'));
    }

    try {
      const response = await fetch(appConfig.getUrl('/update_functional_level'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': `Bearer ${login_token}`
        },
        body: JSON.stringify({
          corporate_account: corporateAccount,
          project_id: currentProject,
          level_id: selectedLevelForEdit.level_id,
          level_description: levelData.level_description
        }),
      });

      const data = await response.json();
      if (data.status === 'Success') {
        // Set success message in main component
        setSuccess('Level updated successfully!');
        setError(''); // Clear any existing errors

        // Clear success message after 3 seconds
        setTimeout(() => setSuccess(''), 3000);

        // Refresh the levels list
        setSelectedRows(new Set());
        fetchLevels(currentParent);

        return Promise.resolve();
      } else {
        return Promise.reject(new Error(data.status_description || 'Failed to update level'));
      }
    } catch (err) {
      return Promise.reject(new Error(err.message || 'Failed to update level'));
    }
  };

  // Row-level edit handler
  const handleRowEdit = (level) => {
    if (permissions.canEdit) {
      setSelectedLevelForEdit(level);
      setIsEditMode(true);
      setIsCreateDialogOpen(true);
    }
  };

  useEffect(() => {
    console.log(`use Effect 5`);
    console.log(`use Effect 5  : ${currentParentDescription}`);
    console.log(`use Effect 5  : ${currentParent}`);
    console.log(`use Effect 5 : ${parentOfParent}`);

    if (corporateAccount && currentProject) {
      console.log('Triggering fetch with:', { corporateAccount, currentProject });
      console.log(`use Effect currentParent  : ${currentParent}`);
      console.log(`use Effect parentOfParent  : ${parentOfParent}`);
      fetchLevels(currentParent);
    }
  }, [corporateAccount, currentProject]);

useEffect(() => {
  console.log(`use Effect 6`);
  console.log(`use Effect 6  : ${currentParentDescription}`);
  console.log(`use Effect 6  : ${currentParent}`);
  console.log(`use Effect 6 : ${parentOfParent}`);

  // Apply sorting to levels when they change
  const sortedLevels = getSortedLevels(levels);
  setFilteredLevels(sortedLevels);
  console.log('Levels updated:', levels); // Debug log
}, [levels, sortField, sortDirection]);



  useEffect(() => {
    console.log(`use Effect 7`);
    console.log(`use Effect 7  : ${currentParentDescription}`);
    console.log(`use Effect 7  : ${currentParent}`);
    console.log(`use Effect 7 : ${parentOfParent}`);

    const setInitialLoad = async () => {
      const current_project = localStorage.getItem('current_project');

      console.log('setInitialLoad', { current_project, currentProject });

      if (!login_token) {
        showErrorDialog('Invalid login credentials. Please login first', '/LoginAndAuthentication');
        return;
      }

      try {
        const result = await getUserInfo({
          login_token: login_token
        });

        console.log('getUserInfo result:', result);

        if (result.success) {
          if (result.userInfo.default_project == 'null' && current_project == 'null') {
            showErrorDialog('Please select a project on the home page', '/RequirementsPortal');
            return;
          }
          setCorporateAccount(result.userInfo.corporate_account);
          setCurrentProject(current_project === 'null' ? result.userInfo.default_project : current_project);
          setUserId(result.user_id);
        } else {
          showErrorDialog('Invalid login credentials. Please login again', '/LoginAndAuthentication');
        }
      } catch (error) {
        console.error('Error calling API:', error);
      }
    };

    setInitialLoad();
  }, []);

  const fetchLevels = async (parent_level_id = null, level_description = '') => {
    if (!permissions.canView) return;

    setSelectedRows(new Set());
    setCurrentPage(1); // Reset to first page on new fetch

    console.log(`Inside fetchLevels Parent_level_id : ${parent_level_id}`);
    console.log(`Inside fetchLevels Current parent : ${currentParent}`);
    console.log(`Inside fetchLevels parent of parent : ${parentOfParent}`);
    console.log(`Inside fetchLevels level_description: ${level_description}`);
    console.log(`Inside fetchLevels currentParentDescription: ${currentParentDescription}`);

    setLoading(true);

    try {
      const response = await fetch(appConfig.getUrl('/get_functional_levels'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': `Bearer ${login_token}`
        },
        body: JSON.stringify({
          corporate_account: corporateAccount,
          project_id: currentProject,
          search_query: searchQuery,
          parent_level_id: parent_level_id
        }),
      });
      console.log(`Helloo..2`);

      const data = await response.json();
      console.log(`Helloo..3`);

      console.log(`after fetchLevels call : ${data}`);
      console.log(`Helloo..4`);

      if (data.status === 'Success' && Array.isArray(data.functional_level_list)) {
        setLevels(data.functional_level_list);
        setFilteredLevels(data.functional_level_list);
        setParentOfParent(data.parent_of_parent);
        setCurrentParent(data.parent_level_id);
        setCurrentParentDescription(data.functional_level_list[0]?.parent_level_description || 'Root Level');
        console.log(`after fetchLevels success currentParent  : ${currentParent}`);
        console.log(`after fetchLevels success parentOfParent  : ${parentOfParent}`);
        console.log(`after fetchLevels success parent_level_id  : ${data.parent_level_id}`);
        console.log(`Inside fetchLevels currentParentDescription: ${currentParentDescription}`);
      } else {
        console.log(`Helloo..5`);
        setLevels([]);
        setFilteredLevels([]);
        if (!data.functional_level_list || data.functional_level_list.length === 0) {
          if (level_description !== 'Delete_Level') {
            setParentOfParent(currentParent);
            setCurrentParent(parent_level_id);
            setCurrentParentDescription(level_description);
          }
          console.log(`Inside fetchLevels upon failure - Current Parent : ${currentParent}`);
          console.log(`Inside fetchLevels upon failure - parentOfParent  : ${parentOfParent}`);
          console.log(`Inside fetchLevels upon failure currentParentDescription: ${currentParentDescription}`);

          if (!searchQuery) {
            setDialogMessage('No Functional Levels found.\nCreate new or copy from predefined list');
//            setShowDialog(true);
          }
        }
      }
    } catch (err) {
      console.log(`Helloo..6`);
      console.error('Fetch error:', err);
      setError(err.message);
      setLevels([]);
      setFilteredLevels([]);
    } finally {
      console.log(`Helloo..7`);
      setLoading(false);
    }
  };

  const fetchDependencies = async () => {
    if (selectedRows.size !== 1) {
      setDialogMessage('Please select exactly one level to view dependencies');
      setShowDialog(true);
      return;
    }

    try {
      const selectedLevelId = Array.from(selectedRows)[0];
      const response = await fetch(appConfig.getUrl('/get_functional_level_dependencies'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': `Bearer ${login_token}`
        },
        body: JSON.stringify({
          corporate_account: corporateAccount,
          project_id: currentProject,
          level_id: selectedLevelId
        }),
      });

      const data = await response.json();
      if (data.status === 'Success') {
        setDependencies(data);
        setShowDependencies(true);
      } else {
        throw new Error(data.status_description || 'Failed to fetch dependencies');
      }
    } catch (err) {
      setDialogMessage('Failed to fetch dependencies: ' + err.message);
      setShowDialog(true);
    }
  };

  const toggleSelectAll = () => {
    if (selectedRows.size === paginatedLevels.length) {
      setSelectedRows(new Set());
    } else {
      const newSelected = new Set(paginatedLevels.map(level => level.level_id));
      setSelectedRows(newSelected);
    }
  };

  const toggleRowSelection = (levelId) => {
    const newSelected = new Set(selectedRows);
    if (newSelected.has(levelId)) {
      newSelected.delete(levelId);
    } else {
      newSelected.add(levelId);
    }
    setSelectedRows(newSelected);
  };



  const executeDelete = async (overrideValue) => {
    console.log('executeDelete called with override:', overrideValue);

    setIsDeleting(true);
    try {
      const requestBody = {
        corporate_account: corporateAccount,
        project_id: currentProject,
        level_ids: Array.from(selectedRows),
        override_dependencies: overrideValue
      };

      console.log('Sending request with body:', requestBody);

      const response = await fetch(appConfig.getUrl('/delete_functional_level'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': `Bearer ${login_token}`
        },
        body: JSON.stringify(requestBody),
      });

      const data = await response.json();
      console.log('API Response:', data);


            if (data.status === 'Success') {
              setSuccess('Levels deleted successfully!');
              setError('');

              // Clear success message after 3 seconds
              setTimeout(() => setSuccess(''), 3000);

              setSelectedRows(new Set());
              fetchLevels(currentParent, 'Delete_Level');
            } else {
              setError(data.status_description || 'Failed to delete levels');
              setSuccess('');
            }


    } catch (err) {
      console.error('Delete error:', err);
        setError('Failed to delete levels: ' + err.message);
  setSuccess('');


    } finally {
      setIsDeleting(false);
    }
  };



  const handleDelete = async () => {
  console.log('Delete initiated');

  if (!permissions.canDelete) {
    setDialogMessage('Insufficient permissions to delete level');
    setShowDialog(true);
    return;
  }

  if (selectedRows.size === 0) {
    setDialogMessage('Please select at least one level to delete');
    setShowDialog(true);
    return;
  }

  // For single selection, show detailed confirmation
  if (selectedRows.size === 1) {
    const levelId = Array.from(selectedRows)[0];
    const level = levels.find(l => l.level_id === levelId);
    setLevelToDelete(level);
    setIsDeleteConfirmOpen(true);
    return;
  }

  // For multiple selections, create a "fake" level object to show the count
  const multipleLevelsObject = {
    level_id: 'multiple',
    level_description: `${selectedRows.size} selected levels`,
    actualLevelIds: Array.from(selectedRows) // Store the real IDs here
  };

  setLevelToDelete(multipleLevelsObject);
  setIsDeleteConfirmOpen(true);
};

const handleDeleteConfirmed = async () => {
  setIsDeleteConfirmOpen(false);
  setIsDeleting(true);

  try {
    // Handle both single and multiple deletions
    const levelIds = levelToDelete.level_id === 'multiple'
      ? levelToDelete.actualLevelIds  // Use the stored actual IDs
      : [levelToDelete.level_id];

    const requestBody = {
      corporate_account: corporateAccount,
      project_id: currentProject,
      level_ids: levelIds,
      override_dependencies: overrideDependencies
    };

    console.log('Sending request with body:', requestBody);

    const response = await fetch(appConfig.getUrl('/delete_functional_level'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'Authorization': `Bearer ${login_token}`
      },
      body: JSON.stringify(requestBody),
    });

    const data = await response.json();
    console.log('API Response:', data);

    if (data.status === 'Success') {
      const message = levelIds.length === 1
        ? 'Level deleted successfully!'
        : 'Levels deleted successfully!';
      setSuccess(message);
      setError('');

      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(''), 3000);

      setSelectedRows(new Set());
      fetchLevels(currentParent, 'Delete_Level');
    } else {
      setError(data.status_description || 'Failed to delete level(s)');
      setSuccess('');
    }

  } catch (err) {
    console.error('Delete error:', err);
    setError('Failed to delete level(s): ' + err.message);
    setSuccess('');
  } finally {
    setIsDeleting(false);
    setLevelToDelete(null);
    setOverrideDependencies(false);
  }
};





const handleDialogClose = () => {
  if (redirectPath) {
    window.location.href = redirectPath;
  }

  if (pendingDeleteAction) {
    pendingDeleteAction();
    setPendingDeleteAction(null);
  }

  setShowDialog(false);
};

const handleDialogCancel = () => {
  setShowDialog(false);
  setPendingDeleteAction(null);
};






  const totalPages = Math.ceil((filteredLevels?.length || 0) / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedLevels = (filteredLevels || []).slice(startIndex, startIndex + itemsPerPage);

  if (loading) return <div>Loading...</div>;

  return (
    <div className="min-h-screen bg-gray-50">
      <Header Project={currentProject} />

      <div className="flex h-[calc(100vh-64px)]">
        <div className="flex-1 overflow-y-auto p-6">
          <div style={styles.container}>

            {deleteError && (
              <div className="mb-4 p-2 rounded bg-red-50 text-red-500">
                {deleteError}
              </div>
            )}

            {/* Title bar */}
            <h1 className="text-l font-bold text-blue-800 mt-6 mr-20 text-center">Manage Functional Hierarchy</h1>
            <div className="flex items-center mb-4 mt-3 justify-between bg-blue-100 p-3 rounded">

              {/* Search bar */}
              <div className="flex items-center">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      handleSearch();
                    }
                  }}
                  placeholder="Search functional level..."
                  className="p-2 pr-10 border rounded w-full"
                />
                <button
                  onClick={handleSearch}
                  className="ml-2 p-2 rounded bg-blue-500 hover:bg-blue-600 text-white"
                >
                  <Search size={18} />
                </button>
              </div>

              <h2 className="text-l font-bold text-blue-700 text-center flex-1">{currentProject}</h2>

              <div className="group relative">
                <button
                  disabled={!permissions.canCreate}
                  onClick={() => {
                    if (permissions.canCreate) {
                      setIsEditMode(false);
                      setSelectedLevelForEdit(null);
                      setIsCreateDialogOpen(true);
                    }
                  }}
                  className={`flex items-center px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 ${!permissions.canCreate ? 'opacity-50' : ''}`}
                >
                  <Plus size={18} className="mr-2" />
                  Create New Level
                </button>

                {/* Create button tooltip */}
                {!permissions.canCreate && (
                  <div className="absolute left-0 top-full mt-2 px-3 py-2 bg-gray-200 text-gray-800 text-xs rounded shadow-lg invisible group-hover:visible whitespace-nowrap z-20">
                    Insufficient permission
                    <div className="absolute bottom-full right-4 w-0 h-0 border-l-4 border-r-4 border-b-4 border-transparent border-b-gray-600"></div>
                  </div>
                )}
              </div>
            </div>
            {/* End Title bar */}

            {!searchQuery && (
              <div className="flex justify-between items-center mb-1 mt-1">
                <div className="text-sm text-gray-600"><strong>Current Parent:</strong> {currentParentDescription}</div>
                {parentOfParent && parentOfParent !== 0 && parentOfParent !== "0" && (
                  <a
                    href="#"
                    className="bg-transparent text-blue-600 flex items-center gap-2 p-2 rounded"
                    onClick={(e) => {
                      e.preventDefault();
                      fetchLevels(parentOfParent);
                    }}
                  >
                    <svg className="w-7 h-7 mr-1" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M14.707 12.707a1 1 0 01-1.414 0L10 9.414l-3.293 3.293a1 1 0 01-1.414-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 010 1.414z" clipRule="evenodd" />
                    </svg>
                    Move Up One Level
                  </a>
                )}
              </div>
            )}

            <table style={styles.table}>
              <thead>



<tr>
  <th style={styles.thNonSortable}>
    <input
      type="checkbox"
      checked={paginatedLevels.length > 0 && selectedRows.size === paginatedLevels.length}
      onChange={toggleSelectAll}
      style={styles.checkbox}
    />
    Select All
  </th>
  <th style={styles.thSortable} onClick={() => handleSort('description')}>
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
      <span>Description{renderSortIcon('description')}</span>
    </div>
  </th>
  <th style={styles.thSortable} onClick={() => handleSort('parent')}>
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
      <span>Parent Level{renderSortIcon('parent')}</span>
    </div>
  </th>
  <th style={styles.thNonSortable}>Actions</th>
</tr>




              </thead>
              <tbody>
                {paginatedLevels.map(level => (
                  <tr key={level.level_id}
                      style={{
                        backgroundColor: selectedRows.has(level.level_id) ? '#DEEFF5' : 'transparent'
                      }}
                      className="border-t hover:bg-gray-50"
                  >
                    <td style={styles.td}>
                      <input
                        type="checkbox"
                        checked={selectedRows.has(level.level_id)}
                        onChange={() => toggleRowSelection(level.level_id)}
                        style={styles.checkbox}
                      />
                    </td>
                    <td style={styles.td}>
                      <a
                        href="#"
                        className="text-blue-600 hover:text-blue-800 hover:underline"
                        onClick={(e) => {
                          e.preventDefault();
                          setSearchQuery(''); // Clear the search text box
                          setCurrentPage(1); // Reset to first page
                          fetchLevels(level.level_id, level.level_description);
                          setCurrentParent(level.level_id);
                          setCurrentParentDescription(level.level_description);
                        }}
                      >
                        {level.level_description}
                      </a>
                    </td>
                    <td style={styles.td}>{level.parent_level_description}</td>
                    {/* NEW: Added Actions column with edit icon */}
                    <td style={styles.td}>
                      <div className="group relative">
                        <button
                          onClick={() => handleRowEdit(level)}
                          disabled={!permissions.canEdit}
                          className={`p-1 rounded hover:bg-blue-100 ${
                            !permissions.canEdit ? 'opacity-50 ' : 'cursor-pointer'
                          }`}
                          style={{ color: permissions.canEdit ? '#4CAF50' : '#999' }}
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                            <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.828-2.828z" />
                          </svg>
                        </button>

                        {/* Edit button tooltip */}
                        {!permissions.canEdit && (
                          <div className="absolute left-0 top-full mt-2 px-3 py-2 bg-gray-200 text-gray-800 text-xs rounded shadow-lg invisible group-hover:visible whitespace-nowrap z-20">
                            Insufficient permission
                            <div className="absolute bottom-full left-4 w-0 h-0 border-l-4 border-r-4 border-b-4 border-transparent border-b-gray-800"></div>
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
                {filteredLevels.length === 0 && (
                  <tr>
                    <td colSpan="4" className="px-4 py-8 text-center text-gray-500">
                      No levels found. Create a new level or adjust your search.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>

            {/* Pagination controls */}
            {!loading && filteredLevels.length > 0 && (
              <div className="mt-4 flex justify-between items-center bg-white p-4 rounded-lg shadow">
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <span>Total Entries: {filteredLevels.length}</span>
                  <span>| Showing {filteredLevels.length > 0 ? startIndex + 1 : 0} - {Math.min(startIndex + itemsPerPage, filteredLevels.length)} of {filteredLevels.length}</span>
                </div>

                <div className="flex items-center gap-2">
                  <button
                    className="p-1 rounded hover:bg-gray-200 disabled:opacity-50"
                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                    disabled={currentPage === 1}
                    aria-label="Previous page"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                  </button>
                  <span className="text-sm text-blue-600">Page {currentPage} of {Math.max(1, totalPages)}</span>
                  <button
                    className="p-1 rounded hover:bg-gray-200 disabled:opacity-50"
                    onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                    disabled={currentPage === totalPages || totalPages === 0}
                    aria-label="Next page"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                </div>

                <div className="flex items-center">
                  <span className="text-sm text-gray-600 mr-2">Items per page:</span>
                  <select
                    value={itemsPerPage}
                    onChange={(e) => {
                      setItemsPerPage(Number(e.target.value));
                      setCurrentPage(1); // Reset to first page when changing items per page
                    }}
                    className="border rounded p-1 text-sm"
                  >
                    <option value={5}>5</option>
                    <option value={10}>10</option>
                    <option value={20}>20</option>
                    <option value={50}>50</option>
                  </select>
                </div>
              </div>
            )}
            {/* End Pagination controls */}

            {/* Error/Success Messages */}
            {(error || success) && (
              <div className={`mb-4 p-3 rounded-md ${
                success
                  ? 'bg-green-50 border border-green-200 text-green-800'
                  : 'bg-red-50 border border-red-200 text-red-800'
              }`}>
                <div className="flex">
                  <div className="flex-shrink-0">
                    {success ? (
                      // Success icon
                      <svg className="h-5 w-5 text-green-400" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                    ) : (
                      // Error icon
                      <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                      </svg>
                    )}
                  </div>
                  <div className="ml-3">
                    <p className="text-sm font-medium">
                      {error || success}
                    </p>
                  </div>
                </div>
              </div>
            )}

            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginTop: '20px',
              width: '100%'
            }}>
              <div style={{ display: 'flex', gap: '10px' }}>
                {/* Delete button with tooltip */}
                <div className="group relative">
                  <button
                    style={{
                      ...styles.deleteButton,
                      padding: '8px 16px',
                      ...(isDeleting || selectedRows.size === 0 || !permissions.canDelete ? styles.disabled : {})
                    }}
                    onClick={handleDelete}
                    disabled={isDeleting || selectedRows.size === 0 || !permissions.canDelete}
                  >
                    {isDeleting ? 'Deleting...' : `Delete Selected (${selectedRows.size})`}
                  </button>

                  {/* Delete button tooltip */}
                  {(selectedRows.size === 0 || !permissions.canDelete) && (
                    <div className="absolute left-0 top-full mt-2 px-3 py-2 bg-gray-200 text-gray-800 text-xs rounded shadow-lg invisible group-hover:visible whitespace-nowrap z-20">
                      {selectedRows.size === 0
                        ? "Select one or more items to delete"
                        : "Insufficient permission"
                      }
                      <div className="absolute bottom-full left-4 w-0 h-0 border-l-4 border-r-4 border-b-4 border-transparent border-b-gray-800"></div>
                    </div>
                  )}
                </div>

                <div className="group relative">
                  <button
                    className={`px-4 py-0 text-white rounded ${
                      selectedRows.size !== 1 ? 'bg-blue-300' : 'bg-blue-500 hover:bg-blue-600'
                    }`}
                    onClick={fetchDependencies}
                    disabled={selectedRows.size !== 1}
                    style={{ height: '28px', fontSize: '0.875rem' }}
                  >
                    View Dependencies
                  </button>

                  {/* View Dependencies button tooltip */}
                  {selectedRows.size !== 1 && (
                    <div className="absolute left-0 top-full mt-2 px-3 py-2 bg-gray-200 text-gray-800 text-xs rounded shadow-lg invisible group-hover:visible whitespace-nowrap z-20">
                      Select exactly one item to view dependencies
                      <div className="absolute bottom-full left-4 w-0 h-0 border-l-4 border-r-4 border-b-4 border-transparent border-b-gray-800"></div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>



      <CreateLevelDialog
        isOpen={isCreateDialogOpen}
        onClose={() => {
          setIsCreateDialogOpen(false);
          setSelectedLevelForEdit(null);
          setIsEditMode(false);
        }}
        onSubmit={isEditMode ? handleEditLevel : handleCreateLevel}
        level={selectedLevelForEdit}
        isEdit={isEditMode}
        existingLevels={levels}
        parentLevelDescription={currentParentDescription}
      />

      <DependencyDialog
        isOpen={showDependencies}
        onClose={() => setShowDependencies(false)}
        dependencies={dependencies}
      />


        {showDialog && (
          <Dialog
            message={dialogMessage}
            onClose={handleDialogClose}
            onCancel={handleDialogCancel}
          />
        )}

        {/* Delete Confirmation Dialog */}
        {renderDeleteConfirmDialog()}



    </div>
  );
};

export default FunctionalLevels;